import { clsx } from 'clsx'
import { type ElementType, type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { Skeleton } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { ProductPicture } from '../../../product'
import {
  recommendationAccessorGetLocalizedProductImageAltText,
  recommendationAccessorGetLocalizedProductImageUrl,
} from '../../model'
import { recommendationAccessorGetSponsoredProductLink } from '../../model/recommendationAccessorGetSponsoredProductLink'
import { useRecommendationWidgetContext } from '../RecommendationAreaProvider'

import { RecommendationProductImageDiscount } from './RecommendationProductImageDiscount'

type RecommendationProductImageProps = {
  className?: string
  product?: GkAirRecommendedProductDataWeb
  slots?: {
    decorator?: ElementType
  }
}

export const RecommendationProductImage: FC<
  RecommendationProductImageProps
> = ({ className, product, slots }) => {
  const { locale } = useGlobalConfigContext()
  const { adServerMetadata } = useRecommendationWidgetContext()

  if (!product) {
    return (
      <Skeleton className="px-7 py-3.5 tablet:p-3.5" variant="rectangular">
        <div className="aspect-square w-full" />
      </Skeleton>
    )
  }

  const Decorator = slots?.decorator ?? RecommendationProductImageDiscount
  const sponsoredProductLink = recommendationAccessorGetSponsoredProductLink({
    adServerMetadata,
    product,
  })

  return (
    <div className={clsx('recommendations-product-image', className)}>
      {sponsoredProductLink ? (
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        <small className="absolute left-1/2 top-0 -translate-x-1/2 text-[#949494]">
          <FormattedMessage id="general.label.sponsored" />
        </small>
      ) : null}
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        className="flex aspect-square size-full max-h-[130px] items-center justify-center rounded-t-m"
        data-qa-id={`form-product-slider-${product.UID}-image-button`}
      >
        <ProductPicture
          className="size-full"
          imageProps={{
            alt: recommendationAccessorGetLocalizedProductImageAltText({
              locale,
              product,
            }),
            className: 'object-contain size-full',
            'data-analytics': 'item-image',
            height: 130,
            src: recommendationAccessorGetLocalizedProductImageUrl({
              locale,
              product,
            }),
            width: 130,
          }}
        />
      </div>
      <Decorator product={product} />
    </div>
  )
}
